import { faFire, faStar, faBookOpen, faTrophy } from '@fortawesome/free-solid-svg-icons';
import { celebrationTheme, dailyCircuitCopy, MILESTONE_LABELS, milestoneNumber } from './milestones';
import styles from '../styles/MilestoneCelebration.module.css';

const ICONS = { streak: faFire, stelline: faStar, learned_terms: faBookOpen, daily_circuit: faTrophy };
const SIZE = 1080;

// Read the same tokens as the modal before it opens; the probe has no visible UI.
export const milestoneTheme = (resource) => {
  const probe = document.createElement('div');
  probe.className = styles[resource];
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;width:0;height:0';
  document.body.appendChild(probe);
  try {
    const computed = window.getComputedStyle(probe);
    const read = (name) => {
      const value = computed.getPropertyValue(`--milestone-${name}`).trim();
      if (!value) throw new Error('milestone_theme_unavailable');
      return value;
    };
    return {
      angle: parseFloat(read('gradient-angle')), start: read('gradient-start'),
      middle: read('gradient-middle'), stop: parseFloat(read('gradient-stop')) / 100,
      end: read('gradient-end'), glow: read('glow'), symbol: read('symbol'),
      numberTop: read('number-top'), numberBottom: read('number-bottom'),
      numberFontFamily: read('number-font-family'), numberFallbackFamily: read('number-fallback-family'),
      numberFontWeight: read('number-font-weight'),
      ...(resource === 'streak' ? { innerFlame: read('inner-flame') } : {}),
      ...(resource === 'stelline' ? { facet: [read('facet-top'), read('facet-middle'), read('facet-bottom')] } : {}),
    };
  } finally { probe.remove(); }
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  const timeout = setTimeout(() => reject(new Error('image_timeout')), 3000);
  image.onload = () => { clearTimeout(timeout); resolve(image); };
  image.onerror = () => { clearTimeout(timeout); reject(new Error('image_load_failed')); };
  image.src = src;
});
const iconImage = (icon, color) => {
  const [width, height, , , paths] = icon.icon;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${(Array.isArray(paths) ? paths : [paths]).map((path) => `<path fill="${color}" d="${path}"/>`).join('')}</svg>`;
  return loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
};

const loadNumberFont = async (theme) => {
  const fallback = theme.numberFallbackFamily;
  if (typeof document.fonts?.load !== 'function') return fallback;
  let timeout;
  try {
    // Load this face even before the modal mounts; unrelated page fonts need not finish.
    const faces = await Promise.race([
      document.fonts.load(`${theme.numberFontWeight} 174px ${theme.numberFontFamily}`, '0123456789./'),
      new Promise((resolve) => { timeout = setTimeout(() => resolve([]), 3000); }),
    ]);
    return faces?.length && faces.every((face) => face.status === 'loaded')
      ? `${theme.numberFontFamily}, ${fallback}` : fallback;
  } catch (_) {
    return fallback;
  } finally { clearTimeout(timeout); }
};

const textBound = (metrics, actual, font, fallback) => {
  if (Number.isFinite(metrics[actual])) return Math.max(0, metrics[actual]);
  if (Number.isFinite(metrics[font])) return Math.max(0, metrics[font]);
  return fallback;
};

const paintBackground = (context, theme, resource) => {
  // CSS angles start at the top and run clockwise; use the square's full extent.
  const angle = theme.angle * Math.PI / 180;
  const dx = Math.sin(angle);
  const dy = -Math.cos(angle);
  const length = SIZE * (Math.abs(dx) + Math.abs(dy));
  const gradient = context.createLinearGradient(540 - dx * length / 2, 540 - dy * length / 2,
    540 + dx * length / 2, 540 + dy * length / 2);
  gradient.addColorStop(0, theme.start);
  gradient.addColorStop(theme.stop, theme.middle);
  gradient.addColorStop(1, theme.end);
  context.fillStyle = gradient;
  context.fillRect(0, 0, SIZE, SIZE);
  const light = context.createRadialGradient(540, 310, 0, 540, 310, 460);
  light.addColorStop(0, '#ffffff22'); light.addColorStop(1, 'transparent');
  context.fillStyle = light;
  context.fillRect(0, 0, SIZE, SIZE);
  if (resource === 'stelline') {
    context.fillStyle = '#e6f8ff';
    context.save(); context.globalAlpha = .7;
    [[.13, .12], [.8, .08], [.29, .27], [.88, .33], [.09, .53], [.76, .61], [.21, .78]].forEach(([x, y]) => {
      context.beginPath(); context.arc(x * SIZE, y * SIZE, 2.5, 0, Math.PI * 2); context.fill();
    });
    context.restore();
  }
};
const paintSymbol = (context, icon, innerFlame, theme, resource) => {
  context.save();
  const glow = context.createRadialGradient(540, 310, 0, 540, 310, 210);
  glow.addColorStop(0, theme.glow); glow.addColorStop(1, 'transparent');
  context.globalAlpha = .32; context.fillStyle = glow;
  context.fillRect(320, 90, 440, 440); context.globalAlpha = 1;
  context.shadowColor = '#0005'; context.shadowOffsetY = 8; context.shadowBlur = 5;
  const [width, height] = ICONS[resource].icon;
  const iconWidth = 210 * width / height;
  context.translate(540, 310);
  if (resource === 'learned_terms') context.rotate(-3 * Math.PI / 180);
  context.drawImage(icon, -iconWidth / 2, -105, iconWidth, 210);
  context.shadowOffsetY = 0; context.shadowBlur = 14; context.shadowColor = theme.glow;
  if (innerFlame) {
    const innerWidth = 114 * width / height;
    context.drawImage(innerFlame, -innerWidth / 2, -15, innerWidth, 114);
  }
  if (resource === 'stelline') {
    const facet = context.createLinearGradient(-85, -110, 85, 110);
    theme.facet.forEach((color, index) => facet.addColorStop([.2, .55, 1][index], color));
    context.fillStyle = facet; context.globalAlpha = .7;
    context.beginPath();
    [[0, -106], [24, -30], [102, -26], [40, 22], [64, 99], [0, 53], [-64, 99], [-40, 22], [-102, -26], [-24, -30]]
      .forEach(([x, y], index) => index ? context.lineTo(x, y) : context.moveTo(x, y));
    context.closePath(); context.fill();
  }
  context.restore();
};
const paintInvitation = (context) => {
  context.save();
  context.shadowColor = '#0003'; context.shadowBlur = 20; context.shadowOffsetY = 6;
  context.fillStyle = '#fff';
  context.beginPath();
  context.moveTo(249, 818); context.lineTo(831, 818);
  context.quadraticCurveTo(870, 818, 870, 857); context.quadraticCurveTo(870, 896, 831, 896);
  context.lineTo(249, 896); context.quadraticCurveTo(210, 896, 210, 857);
  context.quadraticCurveTo(210, 818, 249, 818); context.closePath(); context.fill();
  context.shadowBlur = 0; context.shadowOffsetY = 0;
  context.fillStyle = '#171b20'; context.font = 'bold 32px Arial, sans-serif';
  context.fillText('GIOCA ANCHE TU', 540, 868);
  context.restore();
};

export const createMilestoneCard = async (event) => {
  const resource = celebrationTheme(event);
  const isCircuit = resource === 'daily_circuit';
  const theme = milestoneTheme(resource);
  const [icon, logo, innerFlame, numberFontFamily] = await Promise.all([
    iconImage(ICONS[resource], theme.symbol), loadImage('/gdp_logo_20260111.png'),
    event.resource === 'streak' ? iconImage(faFire, theme.innerFlame) : null,
    isCircuit ? `${theme.numberFontFamily}, ${theme.numberFallbackFamily}` : loadNumberFont(theme),
  ]);
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas_unavailable');
  paintBackground(context, theme, resource);
  paintSymbol(context, icon, innerFlame, theme, resource);
  context.textAlign = 'center';
  context.textBaseline = 'alphabetic';
  context.shadowColor = '#0006'; context.shadowBlur = 4; context.shadowOffsetY = 3;
  const numberGradient = context.createLinearGradient(0, 440, 0, 590);
  numberGradient.addColorStop(0, theme.numberTop); numberGradient.addColorStop(1, theme.numberBottom);
  context.fillStyle = isCircuit ? theme.numberTop : numberGradient;
  if (isCircuit) {
    context.shadowColor = theme.glow; context.shadowBlur = 48; context.shadowOffsetY = 5;
  }
  const { achievement: number, caption } = isCircuit ? dailyCircuitCopy(event)
    : { achievement: milestoneNumber(event), caption: MILESTONE_LABELS[resource].toUpperCase() };
  let numberSize = isCircuit ? 80 : 174;
  context.font = `${theme.numberFontWeight} ${numberSize}px ${numberFontFamily}`;
  let numberMetrics = context.measureText(number);
  // Fit explicitly instead of allowing fillText(maxWidth) to choose a condensed face.
  while (numberMetrics.width > 940 && numberSize > 1) {
    numberSize = Math.max(1, Math.min(numberSize - 1, Math.floor(numberSize * 940 / numberMetrics.width)));
    context.font = `${theme.numberFontWeight} ${numberSize}px ${numberFontFamily}`;
    numberMetrics = context.measureText(number);
  }
  context.fillText(number, 540, 590);
  const numberDescent = textBound(numberMetrics, 'actualBoundingBoxDescent', 'fontBoundingBoxDescent', numberSize * .3);
  context.shadowColor = '#0006'; context.shadowBlur = 4; context.shadowOffsetY = 3;
  context.fillStyle = '#fff'; context.font = 'bold 48px Arial, sans-serif';
  const label = caption;
  const labelAscent = textBound(context.measureText(label), 'actualBoundingBoxAscent', 'fontBoundingBoxAscent', 48);
  context.fillText(label, 540, Math.max(658, 590 + numberDescent + (isCircuit ? 54 : 24) + labelAscent), 940);
  context.shadowBlur = 0; context.shadowOffsetY = 0;
  if (isCircuit) {
    context.drawImage(logo, 502, 925, 76, 76);
  } else {
    paintInvitation(context);
    context.font = 'bold 32px Arial, sans-serif';
    const brandWidth = context.measureText('Giochini di Parole').width;
    const brandLeft = (SIZE - brandWidth - 68) / 2;
    context.drawImage(logo, brandLeft, 925, 52, 52);
    context.textAlign = 'left'; context.fillStyle = '#fff';
    context.fillText('Giochini di Parole', brandLeft + 68, 962);
    context.textAlign = 'center'; context.font = '30px Arial, sans-serif';
    context.fillText('giochinidiparole.com', 540, 1024);
  }
  const blob = await new Promise((resolve, reject) => canvas.toBlob(
    (result) => result ? resolve(result) : reject(new Error('image_export_failed')), 'image/png',
  ));
  return new File([blob], event.kind === 'daily_circuit' ? `giochini-circuito-${event.date}.png`
    : `giochini-${event.resource}-${event.threshold}.png`, { type: 'image/png' });
};
